import { useEffect } from 'react';
import './IceBeamAnimation.css';

/**
 * PROFESSIONAL ICE BEAM ANIMATION
 * Ice-type special move - fires a concentrated beam of freezing energy
 * 
 * Key Features:
 * - Ice-type special attack (90 base power, 100% accuracy)
 * - 10% chance to freeze
 * - Iconic beam attack with crystalline ice stream
 * - Professional gaming quality with frost trail and freeze effect
 */
export default function IceBeamAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    const elements = [];

    // PHASE 1: Ice Energy Charge (0-500ms)
    playerEl.classList.add('ice-beam-user-charge');

    const chargeOrb = document.createElement('div');
    chargeOrb.className = 'ice-beam-charge-orb';
    chargeOrb.style.left = `${playerX - 25}px`;
    chargeOrb.style.top = `${playerY - 25}px`;
    container.appendChild(chargeOrb);
    elements.push(chargeOrb);

    // Frost particles gathering
    for (let i = 0; i < 10; i++) {
      const frost = document.createElement('div');
      frost.className = 'ice-beam-frost-gather';
      const a = (i / 10) * 360;
      const r = 60;
      frost.style.left = `${playerX + Math.cos(a * Math.PI / 180) * r - 4}px`;
      frost.style.top = `${playerY + Math.sin(a * Math.PI / 180) * r - 4}px`;
      frost.style.animationDelay = `${i * 40}ms`;
      frost.style.setProperty('--target-x', `${playerX - (playerX + Math.cos(a * Math.PI / 180) * r)}px`);
      frost.style.setProperty('--target-y', `${playerY - (playerY + Math.sin(a * Math.PI / 180) * r)}px`);
      container.appendChild(frost);
      elements.push(frost);
    }

    // PHASE 2: Ice Beam Stream (500-1200ms)
    const phase2Timer = setTimeout(() => {
      // Main beam line
      const beam = document.createElement('div');
      beam.className = 'ice-beam-stream';
      beam.style.left = `${playerX}px`;
      beam.style.top = `${playerY - 4}px`;
      beam.style.width = `${distance}px`;
      beam.style.transform = `rotate(${angle}deg)`;
      beam.style.transformOrigin = 'left center';
      container.appendChild(beam);
      elements.push(beam);

      // Outer glow beam
      const beamGlow = document.createElement('div');
      beamGlow.className = 'ice-beam-glow';
      beamGlow.style.left = `${playerX}px`;
      beamGlow.style.top = `${playerY - 8}px`;
      beamGlow.style.width = `${distance}px`;
      beamGlow.style.transform = `rotate(${angle}deg)`;
      beamGlow.style.transformOrigin = 'left center';
      container.appendChild(beamGlow);
      elements.push(beamGlow);

      // Ice crystal particles along beam
      for (let i = 0; i < 20; i++) {
        const crystalTimer = setTimeout(() => {
          const crystal = document.createElement('div');
          crystal.className = 'ice-beam-crystal';
          const progress = i / 20;
          crystal.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 20 - 5}px`;
          crystal.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 20 - 5}px`;
          container.appendChild(crystal);
          elements.push(crystal);
        }, i * 30);
        elements.push({ timer: crystalTimer, type: 'timer' });
      }

      // Snowflake particles along beam path
      for (let i = 0; i < 12; i++) {
        const snowTimer = setTimeout(() => {
          const snow = document.createElement('div');
          snow.className = 'ice-beam-snowflake';
          const progress = i / 12;
          snow.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 30 - 4}px`;
          snow.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 30 - 4}px`;
          container.appendChild(snow);
          elements.push(snow);
        }, i * 45);
        elements.push({ timer: snowTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Freeze Impact (1200-1700ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('ice-beam-opponent-hit');

      // Ice explosion at opponent
      const explosion = document.createElement('div');
      explosion.className = 'ice-beam-explosion';
      explosion.style.left = `${opponentX - 100}px`;
      explosion.style.top = `${opponentY - 100}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Ice shard burst
      for (let i = 0; i < 18; i++) {
        const shardAngle = (i / 18) * 360;
        const shardDist = 50 + Math.random() * 45;
        const shard = document.createElement('div');
        shard.className = 'ice-beam-shard-burst';
        shard.style.left = `${opponentX - 4}px`;
        shard.style.top = `${opponentY - 4}px`;
        shard.style.setProperty('--scatter-x', `${Math.cos(shardAngle * Math.PI / 180) * shardDist}px`);
        shard.style.setProperty('--scatter-y', `${Math.sin(shardAngle * Math.PI / 180) * shardDist}px`);
        shard.style.animationDelay = `${i * 15}ms`;
        container.appendChild(shard);
        elements.push(shard);
      }

      // Frost rings
      for (let i = 0; i < 3; i++) {
        const ringTimer = setTimeout(() => {
          const ring = document.createElement('div');
          ring.className = 'ice-beam-frost-ring';
          ring.style.left = `${opponentX - 60}px`;
          ring.style.top = `${opponentY - 60}px`;
          container.appendChild(ring);
          elements.push(ring);
        }, i * 100);
        elements.push({ timer: ringTimer, type: 'timer' });
      }
    }, 1200);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Freeze Aftermath (1700-2000ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('ice-beam-user-charge');

      // Frost crystals lingering on opponent
      for (let i = 0; i < 6; i++) {
        const linger = document.createElement('div');
        linger.className = 'ice-beam-frost-linger';
        linger.style.left = `${opponentX + (Math.random() - 0.5) * 80 - 5}px`;
        linger.style.top = `${opponentY + (Math.random() - 0.5) * 60 - 5}px`;
        linger.style.animationDelay = `${i * 40}ms`;
        container.appendChild(linger);
        elements.push(linger);
      }
    }, 1700);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2000ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('ice-beam-user-charge');
      opponentEl.classList.remove('ice-beam-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 2000);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('ice-beam-user-charge');
      opponentEl.classList.remove('ice-beam-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

IceBeamAnimation.moveType = 'ice';
